type AnalyticsProperties = Record<string, unknown>;

interface AnalyticsClient {
  track: (event: string, properties?: AnalyticsProperties) => void;
  page: (name?: string, properties?: AnalyticsProperties) => void;
  identify: (userId: string, traits?: AnalyticsProperties) => void;
}

declare global {
  interface Window {
    _externalAnalytics?: AnalyticsClient;
    dataLayer?: Record<string, unknown>[];
  }
}

function pushToDataLayer(event: string, properties?: AnalyticsProperties) {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...properties });
  }
}

export const analytics: AnalyticsClient = {
  track: (event: string, properties?: AnalyticsProperties) => {
    try {
      pushToDataLayer(event, properties);
      window._externalAnalytics?.track(event, properties);
    } catch { /* swallow */ }
  },
  page: (name?: string, properties?: AnalyticsProperties) => {
    try {
      pushToDataLayer("page_view", { page_name: name, ...properties });
      window._externalAnalytics?.page(name, properties);
    } catch { /* swallow */ }
  },
  identify: (userId: string, traits?: AnalyticsProperties) => {
    try {
      pushToDataLayer("identify", { user_id: userId, ...traits });
      window._externalAnalytics?.identify(userId, traits);
    } catch { /* swallow */ }
  },
};

export function trackConversion(event: string, properties?: AnalyticsProperties) {
  analytics.track(event, properties);
}

/**
 * Task #2973 — Generate a unique conversion event id. The SAME id is pushed
 * into the dataLayer/Pixel browser event AND sent to the server so Meta and
 * LinkedIn deduplicate the browser event against the server-side
 * Conversions API event (one conversion counted, not two).
 */
export function generateEventId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Read a browser cookie value by name (used for Meta's `_fbp` / `_fbc`). */
export function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  try {
    const match = document.cookie
      .split(/;\s*/)
      .find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
  } catch {
    return undefined;
  }
}
